import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../lib/api'
import { GOAL, reachGoal } from '../lib/metrika'
import { HOME_TITLE, useSeo } from '../lib/seo'
import { FAQ } from '../lib/faq'
import { Brand, ThemeToggle } from '../components/Bits'
import { Account } from '../components/Account'
import HeroDemo from '../components/HeroDemo'
import { seriesStyle } from '../lib/series'
import { useAuth } from '../lib/auth'

export default function Home() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [title, setTitle] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Единственная индексируемая страница: возвращаем ей заголовок и канонический
  // адрес, если человек пришёл сюда из закрытого от индексации эфира.
  useSeo({ title: HOME_TITLE, canonical: '/', noindex: false })

  // Возврат из OAuth приходит сюда же с пояснением, что не получилось.
  const [params] = useSearchParams()
  const loginError =
    params.get('login') === 'failed'
      ? 'Не получилось войти через Яндекс — попробуйте ещё раз'
      : params.get('login') === 'unconfigured'
        ? 'Вход через Яндекс пока не настроен'
        : null

  const create = async () => {
    setBusy(true)
    setError(null)
    try {
      const session = await api.createSession(title.trim() || 'Эфир')
      reachGoal(GOAL.sessionCreated)
      navigate(`/admin/${session.code}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось создать эфир')
    } finally {
      setBusy(false)
    }
  }

  const join = async () => {
    const normalized = code.trim().toUpperCase()
    if (!normalized) return
    setError(null)
    try {
      await api.info(normalized)
      navigate(`/join/${normalized}`)
    } catch {
      setError(`Эфир ${normalized} не найден`)
    }
  }

  return (
    <div className="wrap">
      <header className="row spread">
        <Brand />
        <div className="row" style={{ gap: 10 }}>
          <Link className="account-login" to="/pricing">
            Тарифы
          </Link>
          {user && (
            <Link className="account-login" to="/demos">
              Мои демонстрации
            </Link>
          )}
          <Account />
          <ThemeToggle />
        </div>
      </header>

      <section className="home-hero">
        <div className="col" style={{ gap: 22 }}>
          <h1 className="hero-title">
            {/* Строки разбиты руками: при автопереносе тире уезжало в начало
                следующей строки, а коралловая часть рвалась посередине. */}
            Услышьте
            <br />
            весь зал&nbsp;—
            <br />
            <em>а&nbsp;не&nbsp;первый&nbsp;ряд</em>
          </h1>
          <p className="hero-lead">
            Вопросы задают трое смелых, остальные молчат. С&nbsp;clapme отвечает каждый —
            с&nbsp;телефона, без регистрации, за секунды.
          </p>

          {/* Два входа — два разных человека. Участник пришёл с кодом и спешит,
              поэтому его строка первая и без лишних слов; ведущий пришёл
              готовиться, ему — отдельный блок с пояснением. */}
          <div className="home-actions">
            <div>
              <label className="home-field-label" htmlFor="join-code">
                У меня есть код эфира
              </label>
              <div className="home-join">
                <input
                  id="join-code"
                  placeholder="Код с экрана"
                  value={code}
                  maxLength={6}
                  autoCapitalize="characters"
                  autoComplete="off"
                  spellCheck={false}
                  onChange={(e) => setCode(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && join()}
                />
                <button className="btn" onClick={join}>
                  Войти
                </button>
              </div>
            </div>

            <div className="home-divider">или</div>

            <div className="card col">
              <h2>Провести эфир</h2>
              <input
                aria-label="Название эфира"
                placeholder="Название, например «Итоги квартала»"
                value={title}
                maxLength={120}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && create()}
              />
              <button className="btn btn-primary btn-big" onClick={create} disabled={busy}>
                {busy ? 'Создаём…' : 'Создать эфир'}
              </button>
              <p className="muted tiny">
                Управлять сможете только вы — право ведущего останется в этом браузере.
              </p>
            </div>
          </div>
        </div>

        <HeroDemo />
      </section>

      {(error ?? loginError) && <div className="toast">{error ?? loginError}</div>}

      {/* Ниже — текст для человека, который зашёл разбираться, и для поисковика.
          Он же дублирует заглушку в index.html: краулер без исполнения JS и
          краулер с ним должны видеть одно и то же, иначе это расхождение. */}
      <section className="home-section">
        <h2>Как это работает</h2>
        <ol className="steps">
          <li>
            <span className="step-num" aria-hidden>
              1
            </span>
            <strong>Создайте эфир.</strong>
            <span>
              Получите короткий код и ссылку. Право ведущего останется в этом браузере — никакой
              регистрации.
            </span>
          </li>
          <li>
            <span className="step-num" aria-hidden>
              2
            </span>
            <strong>Покажите QR-код залу.</strong>
            <span>
              Отдельный экран для проектора рисует код крупно; участники сканируют его телефоном
              и сразу оказываются внутри.
            </span>
          </li>
          <li>
            <span className="step-num" aria-hidden>
              3
            </span>
            <strong>Открывайте вопросы.</strong>
            <span>
              Ответы собираются на экране в реальном времени — полоска сбора, облако ответов,
              летящие эмодзи и лидерборд.
            </span>
          </li>
        </ol>
      </section>

      <section className="home-section">
        <h2>Что можно спросить у зала</h2>
        <ul className="steps">
          <li>
            <span className="step-tile" style={seriesStyle(0)} aria-hidden>
              A
            </span>
            <strong>Опрос с вариантами</strong>
            <span>
              С одним ответом или с несколькими. Можно отметить правильный: тогда за него
              начисляются баллы, и чем быстрее ответ, тем больше.
            </span>
          </li>
          <li>
            <span className="step-tile" style={seriesStyle(2)} aria-hidden>
              Аа
            </span>
            <strong>Свободный ответ</strong>
            <span>
              Зал пишет своими словами, ответы складываются в облако. Ведущий может пропускать их
              через премодерацию.
            </span>
          </li>
          <li>
            <span className="step-tile" style={seriesStyle(3)} aria-hidden>
              👏
            </span>
            <strong>Эмодзи-реакции</strong>
            <span>
              По одной реакции в секунду от человека — они летят через экран и подсвечивают его
              тем ярче, чем активнее зал.
            </span>
          </li>
        </ul>
      </section>

      <section className="home-section">
        <h2>Частые вопросы</h2>
        <div className="faq">
          {FAQ.map((item) => (
            <div key={item.q} className="faq-item">
              <h3>{item.q}</h3>
              <p className="muted">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="home-footer">
        <span>
          clapme — интерактив для конференций, лекций, тренингов и корпоративных встреч ·{' '}
          <Link to="/pricing">Тарифы</Link> · <Link to="/legal/agreement">Соглашение</Link> ·{' '}
          <Link to="/legal/requisites">Реквизиты</Link>
        </span>
        {/* Статьи — статичные страницы вне SPA, поэтому обычные <a>: роутер
            про них не знает и заворачивал бы их на главную. */}
        <span>
          Статьи: <a href="/interaktiv-dlya-zala/">интерактив для зала с оплатой из России</a> ·{' '}
          <a href="/opros-zala/">живой опрос зала: как провести</a>
        </span>
      </footer>
    </div>
  )
}
